import { isEmpty } from 'lodash'

/**
 * @param {Array} formikValues
 * @param {Array} questions - Current questions list
 *
 * @returns {Array} The form values object with the question name as `key` and the `value` as value. e.g. { currently_bedside_nursing: 'true' }
 */
export const mappingDataForBranching = (formikValues, questions) => {
	if (!formikValues || isEmpty(formikValues)) return []

	return (
		Object.entries(formikValues).reduce((acc, [key, value]) => {
			const { answer, quizQuestionId } = value || {}
			const questionName = questions.find(({ id }) => id === quizQuestionId)?.name
			acc[questionName] = answer
			return acc
		}, {}) || []
	)
}

/**
 * Hook to check branching logic for the profile completion form
 *
 * @inheritDoc
 * - https://mylong.atlassian.net/browse/WWH-615
 * @param {string} questionName - The current question's name
 * @param {Object} values - The form values object with the question name as `key` and the `value` as value. e.g. { currently_bedside_nursing: 'true' }
 * @return {boolean} True if allowed to show the field, false otherwise
 */
export const useNurseWithNCLEXB = (questionName, values) => {
	if (isEmpty(values)) return true
	if (questionName === 'year_in_current_unit') {
		return values['beside'] === 'true'
	}

	if (questionName === 'back_to_bedside_nursing') {
		return values['beside'] === 'false'
	}
	if (questionName === 'experience_years') {
		return values['currently_bedside_nursing'] === 'true'
	}

	if (questionName === 'have_plan_back_to_bedside_nursing') {
		return values['currently_bedside_nursing'] === 'false'
	}

	if (questionName === 'other_nurse_license_country') {
		return values['have_other_nurse_license_country'] === 'true'
	}

	if (questionName === 'other_nurse_license_countries') {
		return values['has_foreign_nurse_license'] === 'true'
	}

	if (questionName === 'visa_type') {
		return values['have_visited_us'] === 'true' || values['needs_sponsorship'] === 'true'
	}

	if (questionName === 'fall_out_of_status') {
		return values['have_visited_us'] === 'true'
	}
	if (questionName === 'current_visa_type') {
		return values['is_in_us'] === 'true'
	}

	if (questionName === 'visa_expiration_date') {
		return values['is_in_us'] === 'true' || values['needs_sponsorship'] === 'true'
	}

	if (questionName === 'expected_opt_start_date') {
		return values['have_plan_opt'] === 'true'
	}

	if (questionName === 'opt_priority_date') {
		return values['has_employment_based_visa_petition'] === 'true'
	}

	if (questionName === 'visa_type_visited') {
		return values['visited_us'] === 'true'
	}

	if (questionName === 'expected_opt_date') {
		return values['needs_sponsorship'] === 'true' && values['have_plan_opt'] === 'true'
	}

	if (questionName === 'priority_date') {
		return values['employment_petition_filed'] === 'true'
	}

	if (questionName === 'have_plan_opt') {
		return values['needs_sponsorship'] === 'true'
	}

	if (questionName === 'unlawful_status') {
		return values['visited_us'] === 'true'
	}

	return true
}

export const useNurseNonNLCEX = (questionName, values) => {
	if (isEmpty(values)) return true

	if (questionName === 'scheduled_date') {
		return values['scheduled_exam'] === 'true'
	}

	if (questionName === 'has_att') {
		return values['scheduled_exam'] === 'false'
	}

	if (questionName === 'visa_screening_or_english_exam_document') {
		return values['have_aab_ascp_certification'] === 'true' || values['has_started_visa_screening'] === 'true'
	}

	if (questionName === 'year_in_current_unit') {
		return values['beside'] === 'true'
	}

	if (questionName === 'back_to_bedside_nursing') {
		return values['beside'] === 'false'
	}

	if (questionName === 'att_expiration_date') {
		return values['has_att'] === 'true' && values['scheduled_exam'] === 'false'
	}

	if (questionName === 'other_nurse_license_countries') {
		return values['has_foreign_nurse_license'] === 'true'
	}

	if (questionName === 'visa_type_visited') {
		return values['visited_us'] === 'true'
	}

	if (questionName === 'unlawful_status') {
		return values['visited_us'] === 'true'
	}

	if (questionName === 'visa_type') {
		return values['have_visited_us'] === 'true' || values['needs_sponsorship'] === 'true'
	}

	if (questionName === 'visa_expiration_date') {
		return values['needs_sponsorship'] === 'true'
	}

	if (questionName === 'expected_opt_date') {
		return values['needs_sponsorship'] === 'true' && values['have_plan_opt'] === 'true'
	}

	if (questionName === 'priority_date') {
		return values['employment_petition_filed'] === 'true'
	}

	if (questionName === 'have_plan_opt') {
		return values['needs_sponsorship'] === 'true'
	}

	if (questionName === 'unlawful_status') {
		return values['visited_us'] === 'true'
	}
	return true
}

export const useMedTech = (questionName, values) => {
	if (isEmpty(values)) return true

	if (questionName === 'amt_or_ascp_attachment') {
		return values['has_amt_ot_ascp_certification'] === 'true'
	}

	if (questionName === 'visa_screening_or_english_exam_document') {
		return values['has_started_visa_screening'] === 'true'
	}

	return true
}
